import { IMAGE_TO_VIDEO_FREE_TRIAL_PRESET } from '@/ai/video-models';

const PHOTO_TO_ANIMATION_DEFAULT_COST = IMAGE_TO_VIDEO_FREE_TRIAL_PRESET.cost;
const PHOTO_TO_ANIMATION_DEFAULT_SECONDS =
  IMAGE_TO_VIDEO_FREE_TRIAL_PRESET.duration;

export const PHOTO_TO_ANIMATION_PATH = '/photo-to-animation';

export const PHOTO_TO_ANIMATION_DATE_PUBLISHED = '2026-09-22';
export const PHOTO_TO_ANIMATION_LAST_UPDATED = '2026-09-22';
export const PHOTO_TO_ANIMATION_LAST_UPDATED_LABEL = 'September 22, 2026';

export const PHOTO_TO_ANIMATION_H1 =
  'Photo to Animation: Animate a Still Photo';

export const PHOTO_TO_ANIMATION_DEFAULT_PROMPT =
  'Animate this photo as a short illustrated clip: gentle camera drift, painted motion, consistent face and clothing, no text, no logos.';

export const PHOTO_TO_ANIMATION_SEO_TITLE =
  'Photo to Animation | Animate a Still Photo with AI';

export const PHOTO_TO_ANIMATION_SEO_DESCRIPTION =
  'Photo to Animation turns one JPG, PNG, or WebP still photo into a short AI clip. It does not restyle an uploaded video file.';

export const PHOTO_TO_ANIMATION_OFFER_DESCRIPTION = `Default ${PHOTO_TO_ANIMATION_DEFAULT_SECONDS}-second 480p generation costs ${PHOTO_TO_ANIMATION_DEFAULT_COST} credit.`;

export const PHOTO_TO_ANIMATION_KEYWORDS = [
  'photo to animation',
  'animate a photo',
  'photo to animation converter',
  'turn a photo into an animation',
  'animate a still image',
].join(', ');

export const PHOTO_TO_ANIMATION_QUICK_ANSWER = `Photo to Animation converts one still photo into a short animated clip with Grok Imagine 1.5. The default is ${PHOTO_TO_ANIMATION_DEFAULT_SECONDS} seconds at 480p and costs ${PHOTO_TO_ANIMATION_DEFAULT_COST} credit. Upload one JPG, PNG, or WebP. It cannot restyle an existing video file.`;

export const PHOTO_TO_ANIMATION_SPECIFICATIONS = [
  { label: 'Model', value: 'Grok Imagine 1.5' },
  { label: 'Input', value: 'one JPG, PNG, or WebP' },
  { label: 'Duration', value: '1-15 seconds, default 4 seconds' },
  { label: 'Resolution', value: '480p or 720p, default 480p' },
  {
    label: 'Default cost',
    value: `${PHOTO_TO_ANIMATION_DEFAULT_COST} credit for ${PHOTO_TO_ANIMATION_DEFAULT_SECONDS} seconds at 480p`,
  },
  {
    label: 'Output',
    value: 'a short video, not a guaranteed loop or GIF',
  },
] as const;

export const PHOTO_TO_ANIMATION_COMPARISON = [
  {
    label: 'Input',
    photoToAnimation: 'One JPG, PNG, or WebP still photo.',
    imageToVideo: 'One still image on the general workbench.',
    videoExtender: 'An existing MP4, WebM, or MOV clip.',
  },
  {
    label: 'Job',
    photoToAnimation:
      'Animate that still into a short clip. Photo to Animation does not restyle an existing video file.',
    imageToVideo:
      'Generate general motion from a still. Image to Video does not share the Photo to Animation draft.',
    videoExtender:
      'Add 6 or 10 seconds only after a clip exists. AI Video Extender does not animate a still photo.',
  },
  {
    label: 'Length',
    photoToAnimation: '1-15 seconds. The default is 4 seconds at 480p.',
    imageToVideo: 'Depends on the model selected in Image to Video.',
    videoExtender: 'Adds 6 or 10 seconds to an existing clip.',
  },
  {
    label: 'Draft',
    photoToAnimation:
      'Saved at /photo-to-animation with the illustrated-motion prompt.',
    imageToVideo:
      'Saved on Image to Video and kept separate from /photo-to-animation.',
    videoExtender: 'Saved on AI Video Extender after a clip exists.',
  },
] as const;

export const PHOTO_TO_ANIMATION_STEPS = [
  {
    name: 'Upload one photo',
    text: 'Photo to Animation accepts one JPG, PNG, or WebP still that you can publish. A clear face, product, or subject gives the motion something stable to follow.',
  },
  {
    name: 'Set the animation prompt',
    text: `Photo to Animation starts with an illustrated-motion prompt: "${PHOTO_TO_ANIMATION_DEFAULT_PROMPT}" Change the camera, style, or action, and keep the request to one next movement.`,
  },
  {
    name: 'Generate the clip',
    text: 'Sign in if needed, check the credit estimate, then generate. The draft stays at /photo-to-animation and does not replace the Image to Video draft.',
  },
  {
    name: 'Review, extend, or upscale',
    text: 'Download the clip if it holds together. Add time with AI Video Extender, or enlarge it with AI Video Upscaler.',
  },
] as const;

export const PHOTO_TO_ANIMATION_FEATURES = [
  {
    title: 'Start from a photo, not a video file',
    body: 'Photo to Animation accepts one still photo and returns a short clip. It does not ingest an MP4 and redraw every frame.',
  },
  {
    title: 'Keep animation work separate',
    body: 'Prompt, upload, and recent results save at /photo-to-animation. Opening Image to Video does not wipe this draft.',
  },
  {
    title: 'Continue the clip after it exists',
    body: 'Extension and upscaling stay on their own tools. Make the animated clip first, then add seconds or resolution only if the first result is worth keeping.',
  },
] as const;

export const PHOTO_TO_ANIMATION_USE_CASES = [
  {
    title: 'Portrait motion test',
    body: 'Photo to Animation can turn a portrait into a short illustrated clip when you need motion, not a new photoshoot.',
  },
  {
    title: 'Product teaser',
    body: 'Photo to Animation can animate a product still with a small camera move before you decide whether the shot needs a longer edit.',
  },
  {
    title: 'Painting-style movement',
    body: 'Ask Photo to Animation for painted or cel-shaded motion when the photo should read as animation instead of live action.',
  },
] as const;

export const PHOTO_TO_ANIMATION_LIMITS = [
  'Photo to Animation does not convert an uploaded video into a new animation style.',
  'The face, hands, clothes, and background can change. Check the whole clip, not one frame.',
  'The default prompt is a starting point, not a promise of a specific art style.',
  'Credits, safety filters, and your rights to the source photo still apply.',
  'The result is a short video file, not a guaranteed seamless loop or GIF.',
] as const;

export const PHOTO_TO_ANIMATION_FAQS = [
  {
    question: 'What is a photo to animation converter?',
    answer:
      'A photo to animation converter takes one still photo plus a motion prompt and generates a short clip. Photo to Animation does not restyle an existing video file.',
  },
  {
    question: 'Can I animate a photo without a video file?',
    answer:
      'Yes. Photo to Animation can animate a photo without an existing video file. Upload one JPG, PNG, or WebP, describe the motion, and generate.',
  },
  {
    question: 'Does this turn an existing video into animation?',
    answer:
      'No. Photo to Animation does not restyle an existing video file. Use AI Video Extender only after a clip exists and you need 6 or 10 more seconds.',
  },
  {
    question: 'Will the person in the photo stay recognizable?',
    answer:
      'Photo to Animation uses the photo as the start, but the face, hands, clothes, and background can change. Reject the result if the person or product is no longer recognizable.',
  },
  {
    question: 'How is this different from image to video?',
    answer:
      'Photo to Animation has its own illustrated-motion prompt and draft at /photo-to-animation. Image to Video is the general workbench and does not share that draft.',
  },
] as const;

export const PHOTO_TO_ANIMATION_SCHEMA_FEATURES = [
  '1 still image',
  '1-15 seconds',
  '480p or 720p',
] as const;

export function getPhotoToAnimationSeoData() {
  return {
    path: PHOTO_TO_ANIMATION_PATH,
    h1: PHOTO_TO_ANIMATION_H1,
    seoTitle: PHOTO_TO_ANIMATION_SEO_TITLE,
    seoDescription: PHOTO_TO_ANIMATION_SEO_DESCRIPTION,
    keywords: PHOTO_TO_ANIMATION_KEYWORDS,
    datePublished: PHOTO_TO_ANIMATION_DATE_PUBLISHED,
    lastUpdated: PHOTO_TO_ANIMATION_LAST_UPDATED,
    lastUpdatedLabel: PHOTO_TO_ANIMATION_LAST_UPDATED_LABEL,
    quickAnswer: PHOTO_TO_ANIMATION_QUICK_ANSWER,
    offerDescription: PHOTO_TO_ANIMATION_OFFER_DESCRIPTION,
    defaultPrompt: PHOTO_TO_ANIMATION_DEFAULT_PROMPT,
    specifications: PHOTO_TO_ANIMATION_SPECIFICATIONS,
    comparison: PHOTO_TO_ANIMATION_COMPARISON,
    steps: PHOTO_TO_ANIMATION_STEPS,
    features: PHOTO_TO_ANIMATION_FEATURES,
    useCases: PHOTO_TO_ANIMATION_USE_CASES,
    limits: PHOTO_TO_ANIMATION_LIMITS,
    faqs: PHOTO_TO_ANIMATION_FAQS,
    schemaFeatures: PHOTO_TO_ANIMATION_SCHEMA_FEATURES,
  };
}
